import type { StaffOperationsIntentState } from "@lafayette/schemas";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { pillClass } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Icon } from "../../ui/icon";
import { StatusBadge, type StatusTone } from "../../ui/status-badge";
import { stateGlyphs, stateLabels } from "../labels";

// Small parts that the Operations and Diagnostics pages share. A state is an icon AND a word (`StatusBadge`), never colour alone.

const stateTones: Readonly<Record<StaffOperationsIntentState, StatusTone>> = {
  PENDING: "neutral",
  CLAIMED: "info",
  SUCCEEDED: "success",
  FAILED: "danger",
  CANCELLED: "neutral",
};

/** The state of one job: its own icon and its word. */
export function JobStateBadge({ state }: Readonly<{ state: StaffOperationsIntentState }>) {
  return (
    <StatusBadge
      tone={stateTones[state]}
      glyph={<Icon name={stateGlyphs[state]} />}
      className={pillClass}
    >
      {stateLabels[state]}
    </StatusBadge>
  );
}

/** A task family is paused or active. `detail` is the closed reason of a pause, already in words. */
export function TaskStatusBadge({
  paused,
  detail,
}: Readonly<{ paused: boolean; detail?: string | undefined }>) {
  return paused ? (
    <StatusBadge tone="warning" glyph={<Icon name="pause" />} className={pillClass}>
      Paused{detail === undefined ? "" : ` (${detail})`}
    </StatusBadge>
  ) : (
    <StatusBadge tone="success" glyph={<Icon name="play" />} className={pillClass}>
      Active
    </StatusBadge>
  );
}

/**
 * The pages next to Operations (Diagnostics, Audit trail, Runbooks), under the title. They are buttons that wrap on a narrow screen, with
 * space between them: before this component the three words ran together at 390 px.
 */
export function AreaLinks({
  links,
  children,
}: Readonly<{ links: readonly Readonly<{ to: string; label: string }>[]; children?: ReactNode }>) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {links.map((link) => (
        <Button key={link.to} asChild variant="outline" size="sm">
          <Link to={link.to}>{link.label}</Link>
        </Button>
      ))}
      {children}
    </div>
  );
}
